import { t as UsageQuotaService } from "./usage_quota_service-Dyfcck0l.mjs";
import { createInterfaceId } from "@gitlab/needle";
import { gql } from "graphql-request";
import { z } from "zod";
import { DUO_NAMESPACE_NOT_ENTITLED_MESSAGE } from "@gitlab-org/core";
import { err, ok } from "neverthrow";
import * as yaml from "js-yaml";
//#region src/agent_privileges.ts
let AGENT_PRIVILEGES = /* @__PURE__ */ function(AGENT_PRIVILEGES) {
	AGENT_PRIVILEGES[AGENT_PRIVILEGES["READ_WRITE_FILES"] = 1] = "READ_WRITE_FILES";
	AGENT_PRIVILEGES[AGENT_PRIVILEGES["READ_ONLY_GITLAB"] = 2] = "READ_ONLY_GITLAB";
	AGENT_PRIVILEGES[AGENT_PRIVILEGES["READ_WRITE_GITLAB"] = 3] = "READ_WRITE_GITLAB";
	AGENT_PRIVILEGES[AGENT_PRIVILEGES["RUN_COMMANDS"] = 4] = "RUN_COMMANDS";
	AGENT_PRIVILEGES[AGENT_PRIVILEGES["USE_GIT"] = 5] = "USE_GIT";
	AGENT_PRIVILEGES[AGENT_PRIVILEGES["RUN_MCP_TOOLS"] = 6] = "RUN_MCP_TOOLS";
	AGENT_PRIVILEGES[AGENT_PRIVILEGES["START_FLOWS"] = 7] = "START_FLOWS";
	AGENT_PRIVILEGES[AGENT_PRIVILEGES["READ_ONLY_FILES"] = 8] = "READ_ONLY_FILES";
	return AGENT_PRIVILEGES;
}({});
const defaultAgentPrivileges = [
	AGENT_PRIVILEGES.READ_WRITE_FILES,
	AGENT_PRIVILEGES.READ_ONLY_GITLAB,
	AGENT_PRIVILEGES.READ_WRITE_GITLAB,
	AGENT_PRIVILEGES.RUN_COMMANDS,
	AGENT_PRIVILEGES.RUN_MCP_TOOLS,
	AGENT_PRIVILEGES.USE_GIT
];
const HEADLESS_CLI_PRE_APPROVED_AGENT_PRIVILEGES = [
	AGENT_PRIVILEGES.READ_WRITE_FILES,
	AGENT_PRIVILEGES.READ_ONLY_GITLAB,
	AGENT_PRIVILEGES.READ_WRITE_GITLAB,
	AGENT_PRIVILEGES.RUN_COMMANDS,
	AGENT_PRIVILEGES.RUN_MCP_TOOLS,
	AGENT_PRIVILEGES.USE_GIT
];
/**
* Pre-approved set for a normal (non-yolo) interactive build session. Mirrors
* the monolith's `pre_approved_agent_privileges` column default (`{1,2}`) that a
* fresh CLI-created workflow receives: because the CLI always sends
* `agent_privileges`, the monolith skips governance resolution and falls back to
* this default. We send it explicitly so a resume reproduces the fresh-session
* behaviour and can downgrade a workflow that a prior yolo resume left with the
* full pre-approved set.
*/
const BUILD_PRE_APPROVED_AGENT_PRIVILEGES = [AGENT_PRIVILEGES.READ_WRITE_FILES, AGENT_PRIVILEGES.READ_ONLY_GITLAB];
/**
* Read-only privilege set for "plan" mode so the agent can investigate without
* mutating any state.
*
* FIXME: plan mode's toolset (PLAN_ALLOWED_TOOLS in
* packages/cli/src/agents/agents.ts) still exposes `run_git_command`, but this
* set omits USE_GIT (and RUN_COMMANDS). Once privilege gating is authoritative,
* git inspection in plan mode will be rejected server-side. Either add USE_GIT
* here (read-only git inspection is intended) or drop `run_git_command` from the
* plan toolset so the two agree.
*/
const PLAN_AGENT_PRIVILEGES = [AGENT_PRIVILEGES.READ_ONLY_FILES, AGENT_PRIVILEGES.READ_ONLY_GITLAB];
//#endregion
//#region src/graphql/queries.ts
const GET_PROJECT_DATA = gql`
  query getOptionalProjectData($projectPath: ID!, $rootNamespacePath: ID!) {
    project(fullPath: $projectPath) {
      id
      namespace {
        id
      }
    }
    rootNamespace: namespace(fullPath: $rootNamespacePath) {
      id
    }
  }
`;
const GET_WORKFLOW_ENABLEMENT_CHECKS_QUERY = gql`
  query getDuoWorkflowEnablementChecks($projectPath: ID!) {
    project(fullPath: $projectPath) {
      id
      duoWorkflowStatusCheck {
        enabled
        checks {
          name
          value
          message
        }
      }
    }
  }
`;
const GET_WORKFLOW_EVENTS_QUERY = gql`
  query getDuoWorkflowEvents($workflowId: AiDuoWorkflowsWorkflowID!) {
    duoWorkflowEvents(workflowId: $workflowId) {
      nodes {
        checkpoint
        errors
        workflowGoal
        workflowStatus
      }
    }
    duoWorkflowWorkflows(workflowId: $workflowId) {
      nodes {
        id
        status
        project {
          fullPath
        }
      }
    }
  }
`;
const GET_LATEST_CHECKPOINT_QUERY = gql`
  query getLatestCheckpoint($workflowId: AiDuoWorkflowsWorkflowID!) {
    duoWorkflowWorkflows(workflowId: $workflowId) {
      nodes {
        workflowDefinition
        project {
          fullPath
        }
        latestCheckpoint {
          checkpoint
          errors
          workflowGoal
          workflowStatus
        }
      }
    }
  }
`;
const GET_USER_WORKFLOWS = gql`
  query getUserWorkflows($type: String, $after: String, $before: String, $first: Int, $last: Int) {
    duoWorkflowWorkflows(type: $type, first: $first, after: $after, last: $last, before: $before) {
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
      edges {
        node {
          id
          projectId
          humanStatus
          updatedAt
          goal
          stalled
          archived
          ...workflowAgent
          firstCheckpoint {
            checkpoint
          }
        }
      }
    }
  }
`;
const FLOW_METADATA_FRAGMENT = {
	lt: gql`
    fragment flowMetadata on DuoWorkflow {
      id
    }
  `,
	gte: gql`
    fragment flowMetadata on DuoWorkflow {
      flowMetadataId
      flowMetadataVersion
      flowMetadataSchemaVersion
    }
  `,
	version: "19.3.0"
};
const GET_WORKFLOW_FLOW_IDENTITY_QUERY = gql`
  query getWorkflowFlowIdentity($workflowId: AiDuoWorkflowsWorkflowID!) {
    duoWorkflowWorkflows(workflowId: $workflowId) {
      nodes {
        workflowDefinition
        ...flowMetadata
      }
    }
  }
`;
const GET_WORKFLOW_DEFINITION_QUERY = gql`
  query getWorkflowDefinition($workflowId: AiDuoWorkflowsWorkflowID!) {
    duoWorkflowWorkflows(workflowId: $workflowId) {
      nodes {
        workflowDefinition
      }
    }
  }
`;
const AI_CATALOG_VERSION_ID_FRAGMENT = {
	lt: gql`
    fragment workflowAgent on DuoWorkflow {
      id
    }
  `,
	gte: gql`
    fragment workflowAgent on DuoWorkflow {
      aiCatalogItemVersionId
    }
  `,
	version: "18.4.0"
};
const DELETE_DUO_WORKFLOWS_WORKFLOW = gql`
  mutation deleteDuoWorkflowsWorkflow($input: DeleteDuoWorkflowsWorkflowInput!) {
    deleteDuoWorkflowsWorkflow(input: $input) {
      clientMutationId
      errors
      success
    }
  }
`;
const GET_CONFIGURED_AGENTS = gql`
  query getConfiguredAgents($projectId: ProjectID!) {
    ...aiCatalogConfiguredItems
    metadata {
      version
    }
  }
`;
const GET_CONFIGURED_AGENTS_18_4_1_AND_EARLIER = gql`
  fragment aiCatalogConfiguredItems on Query {
    aiCatalogConfiguredItems: aiCatalogItemConsumers(projectId: $projectId, itemType: AGENT) {
      nodes {
        id
        item {
          id
          name
          description
          latestVersion {
            id
          }
        }
      }
    }
  }
`;
const GET_CONFIGURED_AGENTS_18_4_2_AND_LATER = gql`
  fragment aiCatalogConfiguredItems on Query {
    aiCatalogConfiguredItems(projectId: $projectId, itemType: AGENT) {
      nodes {
        id
        pinnedItemVersion @gl_introduced(version: "18.6.0") {
          id
        }
        item {
          id
          name
          description
          latestVersion {
            id
          }
        }
      }
    }
  }
`;
const GET_AGENT_FLOW_CONFIG = gql`
  query getAgentFlowConfig($agentVersionId: AiCatalogItemVersionID!) {
    aiCatalogAgentFlowConfig(agentVersionId: $agentVersionId, flowConfigType: CHAT)
  }
`;
//#endregion
//#region src/workflow_executor_error.ts
var WorkflowExecutorError = class WorkflowExecutorError extends Error {
	constructor(message, statusCode) {
		super(message);
		this.name = "WorkflowExecutorError";
		this.statusCode = statusCode;
		Error.captureStackTrace?.(this, WorkflowExecutorError);
	}
};
//#endregion
//#region src/workflow_message_types.ts
let DuoWorkflowStatus = /* @__PURE__ */ function(DuoWorkflowStatus) {
	DuoWorkflowStatus["CREATED"] = "CREATED";
	DuoWorkflowStatus["RUNNING"] = "RUNNING";
	DuoWorkflowStatus["FINISHED"] = "FINISHED";
	DuoWorkflowStatus["FAILED"] = "FAILED";
	DuoWorkflowStatus["STOPPED"] = "STOPPED";
	DuoWorkflowStatus["INPUT_REQUIRED"] = "INPUT_REQUIRED";
	DuoWorkflowStatus["PLAN_APPROVAL"] = "PLAN_APPROVAL_REQUIRED";
	DuoWorkflowStatus["TOOL_APPROVAL"] = "TOOL_CALL_APPROVAL_REQUIRED";
	return DuoWorkflowStatus;
}({});
const isTerminated = (status) => [
	DuoWorkflowStatus.FINISHED,
	DuoWorkflowStatus.FAILED,
	DuoWorkflowStatus.STOPPED
].includes(status);
const isInProgressState = (status) => [DuoWorkflowStatus.CREATED, DuoWorkflowStatus.RUNNING].includes(status);
const isRunning = (status) => !isTerminated(status);
const isApprovingPlan = (status) => status === DuoWorkflowStatus.PLAN_APPROVAL;
const isNeedsInput = (status) => status === DuoWorkflowStatus.INPUT_REQUIRED;
const isAwaitingToolApproval = (status) => status === DuoWorkflowStatus.TOOL_APPROVAL || status === "REQUIRE_TOOL_CALL_APPROVAL";
const isAwaitingUserInput = (status) => isApprovingPlan(status) || isNeedsInput(status);
/**
* A "stable pause boundary" is a checkpoint where the workflow is parked waiting
* for a human decision: needs input, plan approval, or tool-call approval. Turns
* paused here must not be auto-replayed with an empty goal on a transport error —
* the client should reconnect and wait for the user rather than injecting a
* phantom "reconnect" resume. See gitlab-org/gitlab#602799.
*/
const isAtStablePauseBoundary = (status) => isAwaitingUserInput(status) || isAwaitingToolApproval(status);
let MessageType = /* @__PURE__ */ function(MessageType) {
	MessageType["User"] = "user";
	MessageType["Agent"] = "agent";
	MessageType["Tool"] = "tool";
	return MessageType;
}({});
const isUserMessage = (message) => message.message_type === MessageType.User;
let WorkflowEvent = /* @__PURE__ */ function(WorkflowEvent) {
	WorkflowEvent["PAUSE"] = "pause";
	WorkflowEvent["RESUME"] = "resume";
	WorkflowEvent["STOP"] = "stop";
	WorkflowEvent["MESSAGE"] = "message";
	return WorkflowEvent;
}({});
/**
* A workflow definition is a free-form string the server routes on; it is NOT a
* closed set (arbitrary CLI/rails-supplied definitions flow through verbatim).
* The constant below only names the well-known values for readability.
*/
/** Well-known workflow definition labels. */
const WorkflowDefinition = {
	CHAT: "chat",
	SOFTWARE_DEVELOPMENT: "software_development",
	SOFTWARE_DEVELOPMENT_V1: "software_development/v1",
	SEARCH_AND_REPLACE: "search_and_replace",
	DEVELOPER_V1: "developer/v1"
};
/**
* A missing or empty workflow definition means the legacy chat flow: it is what
* older clients sent before the definition became mandatory, and rails buckets
* `chat` to a shared workflow token. Every consumer must resolve it the same way,
* otherwise the token cache write key and read key drift apart.
*/
const resolveWorkflowDefinition = (workflowDefinition) => workflowDefinition || WorkflowDefinition.CHAT;
/**
* Server-advertised default flow for a new Duo CLI session, delivered as the
* `duo_developer` capability of the `aiFlowsMetadata` GraphQL query. Carries the registry
* coordinates needed to build a RegistryFlowRequest: the mere presence of this
* object means "run the developer flow as the session default", its absence
* means "fall back to legacy chat". The client does not version-check or
* second-guess. The client owns the `workflow_definition` label (used to scope
* the token and detect the flow on resume); the server never sends it.
*/
const defaultFlowConfigSchema = z.object({
	flow_config_id: z.string(),
	flow_config_schema_version: z.string(),
	flow_version: z.string()
});
const nonEmpty = (value) => value && value.trim() !== "" ? value : void 0;
const parseRecordedFlowConfig = (node) => {
	const result = defaultFlowConfigSchema.safeParse({
		flow_config_id: nonEmpty(node?.flowMetadataId),
		flow_config_schema_version: nonEmpty(node?.flowMetadataSchemaVersion),
		flow_version: nonEmpty(node?.flowMetadataVersion)
	});
	return result.success ? result.data : void 0;
};
let WorkflowFilter = /* @__PURE__ */ function(WorkflowFilter) {
	WorkflowFilter["FOUNDATIONAL_CHAT_AGENTS"] = "foundational_chat_agents";
	WorkflowFilter["NON_FOUNDATIONAL_CHAT_AGENTS"] = "non_foundational_chat_agents";
	return WorkflowFilter;
}({});
let ToolApprovalType = /* @__PURE__ */ function(ToolApprovalType) {
	ToolApprovalType["APPROVE_ONCE"] = "approve_once";
	ToolApprovalType["APPROVE_FOR_SESSION"] = "approve-for-session";
	ToolApprovalType["APPROVE_PATTERN_FOR_SESSION"] = "approve-pattern-for-session";
	return ToolApprovalType;
}({});
/**
* Identifies who or what made a tool call approval decision, for audit purposes.
*
* Values name the proximate decision mechanism — the component that actually
* answered the approval request — not the policy content or its author. A
* PreToolUse hook approval is `PreToolUseHook` whether the matched policy is
* GitLab's distributed default policy or a customer-authored local policy;
* policy provenance (author/origin/version of the matched policy) is carried
* separately. Add new values only for genuinely new decision mechanisms
* (e.g. a server-side governance preapproval that bypasses client approval
* entirely), never for new policy origins — every new value must also be
* added to the GraphQL enum, where unknown values fail validation against
* older GitLab instances.
*/
let ApprovalSource = /* @__PURE__ */ function(ApprovalSource) {
	ApprovalSource["UserExplicit"] = "USER_EXPLICIT";
	ApprovalSource["PreToolUseHook"] = "PRETOOLUSE_HOOK";
	ApprovalSource["AutoMode"] = "AUTO_MODE";
	ApprovalSource["PreapprovedConfig"] = "PREAPPROVED_CONFIG";
	return ApprovalSource;
}({});
/**
* Represents approval for a single tool invocation.
* toolArgs are optional since persistence is not required.
*/
/**
* Represents approval for all future invocations of a tool in this session.
* toolArgs are required to enable backend persistence and matching.
*/
/**
* Represents approval for future invocations of a tool matching a glob pattern.
* pattern is the glob string (e.g. "git checkout *") sent to the backend for matching.
*/
/**
* Union of all tool approval types.
* Use discriminated union on `type` field for type narrowing.
*/
/**
* Deliberately carries no `approvalSource`: rejection provenance currently has no
* transport to the backend. The DWS proto `Approval.Rejected` message only has a
* `message` field (no `approval_source`, unlike `Approval.Approved`), and the GraphQL
* `updateDuoWorkflowToolCallApprovals` mutation persists approvals only. Rejection
* actions may still be tagged with an `ApprovalSource` at the action level (see
* `ToolApprovalAction`) for local logging; that tag is dropped here by design.
*/
function isWorkflowExecutorErrorEvent(event) {
	return event instanceof WorkflowExecutorError;
}
/**
* Type guard for `DuoWorkflowEvent`. Discriminates on `workflowStatus`, which
* other stream events (`WorkflowExecutorError`, `WorkflowRetryEvent`) do not carry.
*/
function isDuoWorkflowEvent(event) {
	return typeof event === "object" && event !== null && "workflowStatus" in event && Object.values(DuoWorkflowStatus).includes(event.workflowStatus);
}
function isWorkflowRetryEvent(event) {
	if (typeof event !== "object" || event === null || !("kind" in event)) return false;
	const candidate = event;
	return candidate.kind === "retry" && typeof candidate.attempt === "number" && typeof candidate.maxAttempts === "number" && typeof candidate.backoffMs === "number";
}
/** Union of everything an executor can yield over the workflow stream.
* Server originating DuoWorkflowEvent, and client synthesized WorkflowExecutorError + WorkflowRetryEvent */
let DuoWorkflowStatusEvent = /* @__PURE__ */ function(DuoWorkflowStatusEvent) {
	DuoWorkflowStatusEvent["START"] = "start";
	DuoWorkflowStatusEvent["PAUSE"] = "pause";
	DuoWorkflowStatusEvent["RESUME"] = "resume";
	DuoWorkflowStatusEvent["FINISH"] = "finish";
	DuoWorkflowStatusEvent["DROP"] = "drop";
	return DuoWorkflowStatusEvent;
}({});
const WorkflowSuccessCode = 0;
let WorkflowStatusCode = /* @__PURE__ */ function(WorkflowStatusCode) {
	WorkflowStatusCode[WorkflowStatusCode["GENERAL_FAILURE"] = 1] = "GENERAL_FAILURE";
	WorkflowStatusCode[WorkflowStatusCode["FAILED_TO_START"] = 2] = "FAILED_TO_START";
	WorkflowStatusCode[WorkflowStatusCode["AUTH_TOKEN_ERROR"] = 3] = "AUTH_TOKEN_ERROR";
	WorkflowStatusCode[WorkflowStatusCode["CREATION_FAILED"] = 4] = "CREATION_FAILED";
	WorkflowStatusCode[WorkflowStatusCode["FAILED_TO_START_ALT"] = 5] = "FAILED_TO_START_ALT";
	WorkflowStatusCode[WorkflowStatusCode["SERVICE_CONNECTION_FAILED"] = 6] = "SERVICE_CONNECTION_FAILED";
	WorkflowStatusCode[WorkflowStatusCode["AUTH_TOKEN_FETCH_ERROR"] = 50] = "AUTH_TOKEN_FETCH_ERROR";
	WorkflowStatusCode[WorkflowStatusCode["INVALID_API_CONFIGURATION"] = 51] = "INVALID_API_CONFIGURATION";
	WorkflowStatusCode[WorkflowStatusCode["MISSING_CERTIFICATE_SETTINGS"] = 53] = "MISSING_CERTIFICATE_SETTINGS";
	WorkflowStatusCode[WorkflowStatusCode["SERVICE_CONNECTION_DROPPED"] = 54] = "SERVICE_CONNECTION_DROPPED";
	WorkflowStatusCode[WorkflowStatusCode["SERVICE_CONNECTION_CLOSED_MESSAGE_TOO_BIG"] = 55] = "SERVICE_CONNECTION_CLOSED_MESSAGE_TOO_BIG";
	WorkflowStatusCode[WorkflowStatusCode["SERVICE_CONNECTION_INTERNAL_ERROR"] = 56] = "SERVICE_CONNECTION_INTERNAL_ERROR";
	WorkflowStatusCode[WorkflowStatusCode["SERVICE_CONNECTION_BAD_GATEWAY"] = 57] = "SERVICE_CONNECTION_BAD_GATEWAY";
	WorkflowStatusCode[WorkflowStatusCode["SERVICE_CONNECTION_TLS_HANDSHAKE"] = 58] = "SERVICE_CONNECTION_TLS_HANDSHAKE";
	WorkflowStatusCode[WorkflowStatusCode["SERVICE_CONNECTION_UNSUPPORTED_DATA_TYPE"] = 59] = "SERVICE_CONNECTION_UNSUPPORTED_DATA_TYPE";
	WorkflowStatusCode[WorkflowStatusCode["LOCKED_SOCKET"] = 60] = "LOCKED_SOCKET";
	WorkflowStatusCode[WorkflowStatusCode["USAGE_QUOTA_EXCEEDED"] = 62] = "USAGE_QUOTA_EXCEEDED";
	WorkflowStatusCode[WorkflowStatusCode["NO_DUO_ACCESS"] = 63] = "NO_DUO_ACCESS";
	WorkflowStatusCode[WorkflowStatusCode["BAD_REQUEST"] = 64] = "BAD_REQUEST";
	return WorkflowStatusCode;
}({});
const WorkflowStatusCodeMessages = {
	[WorkflowStatusCode.GENERAL_FAILURE]: "Your request was valid but Workflow failed to complete it. Please try again.",
	[WorkflowStatusCode.FAILED_TO_START]: "Workflow failed to start.",
	[WorkflowStatusCode.AUTH_TOKEN_ERROR]: "Workflow could not use your token to connect to your GitLab instance.",
	[WorkflowStatusCode.CREATION_FAILED]: "The service could not create the workflow.",
	[WorkflowStatusCode.FAILED_TO_START_ALT]: "Workflow failed to start.",
	[WorkflowStatusCode.SERVICE_CONNECTION_FAILED]: "Workflow could not connect to the Workflow service. See also https://docs.gitlab.com/user/duo_agent_platform/troubleshooting/#network-issues",
	[WorkflowStatusCode.AUTH_TOKEN_FETCH_ERROR]: "An error occurred while fetching an authentication token for this workflow.",
	[WorkflowStatusCode.INVALID_API_CONFIGURATION]: "GitLab API configuration details (instanceUrl and token) are unavailable in the extension. Restart your editor and try again.",
	[WorkflowStatusCode.MISSING_CERTIFICATE_SETTINGS]: "The client has not been configured to trust the server's SSL certificate. If you are using a proxy or custom certificate authority you must configure this. See also https://docs.gitlab.com/editor_extensions/language_server/#configure-the-language-server-to-use-a-proxy",
	[WorkflowStatusCode.SERVICE_CONNECTION_DROPPED]: "The connection to the server was dropped or abnormally closed. This could be due to an intermittent network connectivity issue.",
	[WorkflowStatusCode.SERVICE_CONNECTION_CLOSED_MESSAGE_TOO_BIG]: "The connection to the server was closed because the message was too large.",
	[WorkflowStatusCode.SERVICE_CONNECTION_INTERNAL_ERROR]: "The connection to the server was closed because the server encountered an internal error.",
	[WorkflowStatusCode.SERVICE_CONNECTION_BAD_GATEWAY]: "The connection to the server was closed because the server encountered an error from an upstream service (502 bad gateway).",
	[WorkflowStatusCode.SERVICE_CONNECTION_TLS_HANDSHAKE]: "The connection to the server was closed due to a failure to perform a TLS handshake (server certificate cannot be verified).",
	[WorkflowStatusCode.SERVICE_CONNECTION_UNSUPPORTED_DATA_TYPE]: "The connection to the server was closed because the message contained an unsupported data type.",
	[WorkflowStatusCode.USAGE_QUOTA_EXCEEDED]: `No credits remain for this billing period.\n\nContact your administrator for more credits, or switch to Non-Agentic Chat to continue.\n\nWhen you have more credits, refresh.`,
	[WorkflowStatusCode.LOCKED_SOCKET]: "GitLab Duo is already responding to this chat in another tab or location. Start a new chat, or wait for GitLab Duo to finish before sending a new message.",
	[WorkflowStatusCode.NO_DUO_ACCESS]: DUO_NAMESPACE_NOT_ENTITLED_MESSAGE,
	[WorkflowStatusCode.BAD_REQUEST]: "GitLab Duo is paused waiting for your input. Type your message to continue."
};
const generateErrorMessageFromStatusCode = (statusCode) => {
	if (statusCode === null || !(statusCode in WorkflowStatusCodeMessages)) return WorkflowStatusCodeMessages[WorkflowStatusCode.GENERAL_FAILURE];
	return WorkflowStatusCodeMessages[statusCode];
};
const TRANSIENT_STATUS_CODES = new Set([
	WorkflowStatusCode.SERVICE_CONNECTION_DROPPED,
	WorkflowStatusCode.SERVICE_CONNECTION_CLOSED_MESSAGE_TOO_BIG,
	WorkflowStatusCode.SERVICE_CONNECTION_BAD_GATEWAY,
	WorkflowStatusCode.SERVICE_CONNECTION_TLS_HANDSHAKE,
	WorkflowStatusCode.SERVICE_CONNECTION_UNSUPPORTED_DATA_TYPE,
	WorkflowStatusCode.LOCKED_SOCKET,
	WorkflowStatusCode.USAGE_QUOTA_EXCEEDED,
	WorkflowStatusCode.BAD_REQUEST
]);
/**
* Whether a failure code leaves the server-side workflow alive and resumable
* (connection-level drops, locked socket, quota exhaustion) rather than killing
* it. Callers use this to decide whether to keep reusing the same workflow.
*
* Conservative allowlist: any code not listed defaults to "not transient", so a
* newly added enum member is treated as fatal until explicitly added here. That
* default favours starting a fresh workflow over silently reusing a dead one;
* connection-level codes that should preserve the workflow must be added above.
*/
const isTransientStatusCode = (statusCode) => TRANSIENT_STATUS_CODES.has(statusCode);
//#endregion
//#region src/checkpoint_utils.ts
const parseLangGraphCheckpoint = (langGraphCheckpoint) => {
	try {
		return JSON.parse(langGraphCheckpoint);
	} catch {
		throw new Error("Failed to parse checkpoint");
	}
};
/**
* When a workflow/chat is first started, the latestCheckpoint returned from backend is null,
* so we default to this initial hardcoded RUNNING checkpoint until we get our next actual
* checkpoint returned.
*/
const createDefaultWorkflowEvent = () => {
	return {
		errors: [],
		workflowStatus: DuoWorkflowStatus.RUNNING,
		checkpoint: {
			ts: (/* @__PURE__ */ new Date()).toISOString(),
			channel_values: { status: "Planning" }
		},
		workflowGoal: ""
	};
};
const parseLatestCheckpointWorkflowData = (response) => {
	try {
		const { latestCheckpoint } = response?.duoWorkflowWorkflows?.nodes?.[0] ?? {};
		if (!latestCheckpoint) return ok(createDefaultWorkflowEvent());
		const parsedCheckpoint = parseLangGraphCheckpoint(latestCheckpoint.checkpoint);
		return ok({
			...latestCheckpoint,
			checkpoint: parsedCheckpoint
		});
	} catch (error) {
		return err(/* @__PURE__ */ new Error(`Failed to parse latest workflow checkpoint: ${error instanceof Error ? error.message : String(error)}`));
	}
};
const getStatus = (duoWorkflowEvent) => {
	return duoWorkflowEvent.workflowStatus;
};
//#endregion
//#region src/workflow_event_utils.ts
const generateGraphqlWorkflowId = (workflowId) => {
	return `gid://gitlab/Ai::DuoWorkflows::Workflow/${workflowId}`;
};
const parseWorkflowEventsResponse = (response) => {
	return response?.duoWorkflowEvents?.nodes || [];
};
const getLatestEvent = (response) => {
	const events = parseWorkflowEventsResponse(response);
	if (events.length === 0) return null;
	return events[0] ?? null;
};
//#endregion
//#region src/ui_chat_log.ts
const ToolInfoArgsSchema = z.record(z.string(), z.unknown());
const ToolResponseSchema = z.object({
	content: z.string(),
	additional_kwargs: z.record(z.string(), z.unknown()),
	response_metadata: z.record(z.string(), z.unknown()),
	type: z.string(),
	name: z.string(),
	id: z.string().nullable(),
	tool_call_id: z.string(),
	artifact: z.unknown(),
	status: z.string()
});
const ToolInfoSchema = z.object({
	name: z.string(),
	args: ToolInfoArgsSchema,
	tool_response: z.union([ToolResponseSchema, z.string()]).optional(),
	suggested_patterns: z.array(z.string()).optional()
});
/** Collapses absent, null and empty into one "no value" case: `undefined`. */
const OptionalNameSchema = z.string().nullish().transform((value) => value || void 0).optional();
/**
* Accepts both shapes the id arrives in, and yields the one consumers compare against.
* An empty string is no id — folded into `undefined` like OptionalNameSchema does — but
* the check is explicit rather than `value || undefined`, which would erase a numeric 0.
*/
const SubsessionIdSchema = z.union([z.string(), z.number()]).nullish().transform((value) => {
	if (value === null || value === void 0 || value === "") return void 0;
	return String(value);
}).optional();
const BaseMessageSchema = z.object({
	message_id: z.string().optional(),
	message_sub_type: z.string().nullable(),
	content: z.string(),
	timestamp: z.string(),
	status: z.string().nullable(),
	correlation_id: z.string().nullable(),
	additional_context: z.unknown(),
	component_name: OptionalNameSchema,
	subsession_id: SubsessionIdSchema
});
const WorkflowMessageSchema = BaseMessageSchema.extend({
	message_type: z.enum(["user", "agent"]),
	tool_info: z.null()
});
const WorkflowPlanApprovalRequestSchema = BaseMessageSchema.extend({
	message_type: z.literal("request"),
	message_sub_type: z.literal("approval"),
	tool_info: z.null()
});
const WorkflowRequestSchema = BaseMessageSchema.extend({
	message_type: z.literal("request"),
	tool_info: ToolInfoSchema
});
const WorkflowToolSchema = BaseMessageSchema.extend({
	message_type: z.literal("tool"),
	tool_info: z.union([ToolInfoSchema, z.null()])
});
const ChatLogSchema = z.union([
	WorkflowMessageSchema,
	WorkflowPlanApprovalRequestSchema,
	WorkflowRequestSchema,
	WorkflowToolSchema
]);
function isPlanApprovalRequest(entry) {
	return entry.message_type === "request" && entry.message_sub_type === "approval" && entry.tool_info === null;
}
/** A node run is open from its `started` event until the terminal event with same id. */
const NodeEventSchema = z.object({
	run_id: z.string(),
	component: z.string(),
	phase: z.enum([
		"started",
		"ended",
		"errored"
	])
});
/**
* Sent incrementally, so callers must accumulate across checkpoints. Validated
* element-wise and non-throwing: a supplementary signal must never be able to discard
* the conversation it arrived with, so a malformed event is dropped, not an error.
*/
function nodeEventsFrom(checkpoint) {
	const raw = checkpoint?.channel_values?.node_events;
	if (!Array.isArray(raw)) return [];
	return raw.flatMap((event) => {
		const parsed = NodeEventSchema.safeParse(event);
		return parsed.success ? [parsed.data] : [];
	});
}
function extractUiChatLog(message) {
	return parseCheckpoint(message).map(({ chatLog }) => chatLog);
}
function parseCheckpoint(message) {
	if (!message.checkpoint) return ok({
		chatLog: [],
		nodeEvents: []
	});
	let checkpoint;
	try {
		checkpoint = JSON.parse(message.checkpoint);
	} catch (e) {
		return err(new Error(`Failed to parse a workflow checkpoint. Checkpoint: ${message.checkpoint}`, { cause: e }));
	}
	const nodeEvents = nodeEventsFrom(checkpoint);
	const uiChatLog = checkpoint?.channel_values?.ui_chat_log;
	if (!uiChatLog || !Array.isArray(uiChatLog)) return ok({
		chatLog: [],
		nodeEvents
	});
	const validatedMessages = [];
	for (let i = 0; i < uiChatLog.length; i++) {
		const rawMessage = uiChatLog[i];
		const parseResult = ChatLogSchema.safeParse(rawMessage);
		if (!parseResult.success) return err(/* @__PURE__ */ new Error(`Failed to validate message at index ${i}: ${parseResult.error.message}. Raw message: ${JSON.stringify(rawMessage)}`));
		validatedMessages.push(parseResult.data);
	}
	return ok({
		chatLog: validatedMessages,
		nodeEvents
	});
}
//#endregion
//#region src/delegation.ts
/** `message_sub_type` values marking a delegation's boundaries in the shared chat log. */
const DELEGATION_SUB_TYPE = "delegation";
const DELEGATION_RETURNS_SUB_TYPE = "delegation_returns";
const DELEGATION_ERROR_SUB_TYPE = "delegation_error";
const DELEGATE_TASK_TOOL_NAME = "delegate_task";
/** The id has been seen under either key, and as a number rather than a string. */
const SUBSESSION_ARG_KEYS = ["subsession_id", "session_id"];
function readName(value) {
	return typeof value === "string" && value.length > 0 ? value : void 0;
}
function readSubsessionId(args) {
	for (const key of SUBSESSION_ARG_KEYS) {
		const value = args[key];
		if (typeof value === "string" && value.length > 0) return value;
		if (typeof value === "number" && Number.isFinite(value)) return String(value);
	}
}
/**
* The delegation boundary an entry marks, or undefined for an ordinary entry. Either name
* may be absent: a `delegation_error` carries empty args, so the caller resolves it from
* the open sub-session instead.
*/
function parseDelegationMarker(entry) {
	const phase = delegationPhase(entry.message_sub_type);
	if (!phase) return void 0;
	const args = entry.tool_info?.args ?? {};
	return {
		phase,
		subagentName: readName(args.subagent_name),
		subsessionId: readSubsessionId(args) ?? entry.subsession_id,
		prompt: readName(args.prompt)
	};
}
function parseStreamingDelegation(entry) {
	if (entry.tool_info?.name !== "delegate_task") return void 0;
	const { args } = entry.tool_info;
	const subagentName = readName(args.subagent_name);
	if (subagentName === void 0) return void 0;
	return {
		phase: "started",
		subagentName,
		subsessionId: readSubsessionId(args),
		prompt: readName(args.prompt)
	};
}
function delegationPhase(messageSubType) {
	switch (messageSubType) {
		case DELEGATION_SUB_TYPE: return "started";
		case DELEGATION_RETURNS_SUB_TYPE: return "returned";
		case DELEGATION_ERROR_SUB_TYPE: return "failed";
		default: return;
	}
}
//#endregion
//#region src/session_export/session_export_schema.ts
/**
* Versioned envelope for a portable session export bundle.
*
* Not a sanitised artifact: paths are anchored and credentials redacted, but
* host detail survives inside tool output.
*/
/** Current schema version. Bump when the bundle shape changes incompatibly. */
const SESSION_EXPORT_SCHEMA_VERSION = "1.0";
/**
* Stands in for the exporting machine's workspace root, re-anchored by
* `resolveExportedPath`. Literal occurrences are not escaped, so adding
* escaping would be a `schemaVersion` change.
*/
const ROOT_PLACEHOLDER = "<gl-duo-root>";
const toolResponseSchema = z.object({
	/** Normalised: an object on the standard flow, a bare string on the developer one. */
	content: z.string(),
	status: z.string().optional(),
	truncated: z.boolean().optional(),
	/** Measured after anchoring and redaction, so it is comparable to `content`. */
	originalLength: z.number().int().nonnegative().optional()
});
/** Raw backend payload: `args` differs per tool and flow, so it is unvalidated. */
const toolCallSchema = z.object({
	name: z.string(),
	args: z.record(z.string(), z.unknown()),
	/** Redacted, so unsafe to reuse as approval config: a mask reads as a wildcard. */
	suggestedPatterns: z.array(z.string()).optional(),
	response: toolResponseSchema.optional(),
	/** Cut `args` strings, keyed by RFC 6901 pointer: a marker inside an opaque record would read as content. */
	argsTruncated: z.record(z.string(), z.number().int().nonnegative()).optional()
});
/**
* Drops `additional_context` (host paths), the entry-level `status` (says
* nothing) and `correlation_id` (plumbing). A field left out of a bundle cannot
* be added back afterwards.
*/
const entryBaseShape = {
	/** Not unique, and not a pairing key: pair on tool name and args instead. */
	id: z.string(),
	/** ISO-8601, as recorded by the backend. */
	timestamp: z.string(),
	content: z.string(),
	contentTruncated: z.boolean().optional(),
	/** As on a tool response: UTF-16 code units, where the bundle budget is bytes. */
	contentOriginalLength: z.number().int().nonnegative().optional(),
	subType: z.string().optional(),
	/** Sub-agent attribution. Absent means unknown, not "the parent". */
	agent: z.string().optional(),
	subsessionId: z.string().optional()
};
/** `request` and `tool` stay distinct: a session can end on an unanswered request. */
const knownEntrySchema = z.discriminatedUnion("type", [
	z.object({
		...entryBaseShape,
		type: z.literal("user")
	}),
	z.object({
		...entryBaseShape,
		type: z.literal("agent")
	}),
	z.object({
		...entryBaseShape,
		type: z.literal("request"),
		tool: toolCallSchema.optional()
	}),
	z.object({
		...entryBaseShape,
		type: z.literal("tool"),
		tool: toolCallSchema.optional()
	})
]);
/** Derived from the union so the catch-all below cannot drift from it. */
const KNOWN_ENTRY_TYPES = knownEntrySchema.options.map((option) => option.shape.type.value);
/** Unknown types pass through; the refinement keeps known ones strictly validated. */
const unknownEntrySchema = z.object({
	id: z.string(),
	timestamp: z.string(),
	type: z.string().refine((value) => !KNOWN_ENTRY_TYPES.includes(value), { message: "known entry type must match its strict schema" })
}).passthrough();
const transcriptEntrySchema = z.union([knownEntrySchema, unknownEntrySchema]);
/**
* Describes the transcript, not the session. `text` awaits a summarizer; every
* other field is derived mechanically by `deriveSessionSummary`.
*
* Copies transcript bytes, so it counts against the budget and is derived last.
*/
const summarySchema = z.object({
	text: z.string().optional(),
	entryCount: z.number().int().nonnegative(),
	/** Executions, not calls: an abandoned `request` counts zero. */
	toolExecutionCount: z.number().int().nonnegative(),
	/** Bounds of the retained transcript, not of the session. */
	firstEntryAt: z.string().optional(),
	lastEntryAt: z.string().optional(),
	/** Character-identical to the transcript; every user entry counts, `/compact` too. */
	userGoals: z.array(z.string()),
	/** Deduplicated, first-seen order, successful executions only. */
	filesRead: z.array(z.string()),
	filesChanged: z.array(z.string()),
	/** Keeps failed commands, unlike the file lists: a non-zero exit still ran. */
	commandsRun: z.array(z.string()),
	/** Only failures with no later success of the same tool and arguments. */
	unresolvedErrors: z.array(z.object({
		tool: z.string(),
		entryId: z.string(),
		message: z.string()
	})),
	/** Trailing request only: a rejection is recorded identically to no answer. */
	pendingToolCalls: z.array(z.object({
		tool: z.string(),
		entryId: z.string()
	})),
	/** Last `agent` entry whatever its sub-type, `reasoning` included. */
	finalAgentMessage: z.string().optional()
});
const sessionExportBundleSchema = z.object({
	schemaVersion: z.literal("1.0"),
	exportedAt: z.string(),
	session: z.object({
		/** The one field never truncated or dropped: a cut id names a different session, or none. */
		id: z.string(),
		title: z.string().optional(),
		titleTruncated: z.boolean().optional(),
		/** As on a tool response: UTF-16 code units. */
		titleOriginalLength: z.number().int().nonnegative().optional(),
		flow: z.string().optional(),
		/** Dropped rather than cut: a truncated flow name reads as a different valid one. */
		flowOmitted: z.boolean().optional()
	}),
	workspace: z.object({
		/** The folder name only; the path leading to it is never recorded. */
		rootName: z.string(),
		/** No length recorded: this is a label, so all a reader needs is that it is not verbatim. */
		rootNameTruncated: z.boolean().optional()
	}),
	/** Absent when per-field caps sufficed. Omission is a contiguous prefix, so a count is complete. */
	budget: z.object({
		maxBytes: z.number().int().positive(),
		omittedEntryCount: z.number().int().nonnegative(),
		/** Set when the bounded envelope alone exceeds `maxBytes`, which no trimming can fix. */
		exceeded: z.boolean().optional()
	}).optional(),
	summary: summarySchema,
	/** Carried alongside `summary` deliberately; dropping it later would be breaking. */
	transcript: z.array(transcriptEntrySchema)
});
/** Every field but `id`/`timestamp`/`type` widens to `unknown`; narrow with {@link isKnownEntry}. */
/** A transcript entry of a `type` this version understands, with real field types. */
/** Narrows to the strict union, which discriminates properly on `type`. */
function isKnownEntry(entry) {
	return KNOWN_ENTRY_TYPES.includes(entry.type);
}
const VERSION_PATTERN = /^\d+\.\d+$/;
/** Requires a {@link VERSION_PATTERN} match; there is no fallback for garbage. */
const parseVersion = (version) => {
	const [major, minor] = version.split(".");
	return [Number.parseInt(major, 10), Number.parseInt(minor, 10)];
};
const isNewerThanSupported = (version) => {
	const [major, minor] = parseVersion(version);
	const [supportedMajor, supportedMinor] = parseVersion("1.0");
	return major > supportedMajor || major === supportedMajor && minor > supportedMinor;
};
/**
* Renders at most three zod issues as `path: message`, never a raw zod dump.
* Copied from `summarizeZodIssues` in `lib_sandbox`, which this package must
* not depend on.
*/
const describeIssues = (error) => {
	const shown = error.issues.slice(0, 3).map((issue) => `${issue.path.join(".") || "bundle"}: ${issue.message}`).join("; ");
	const remaining = error.issues.length - 3;
	return remaining > 0 ? `${shown} (and ${remaining} more)` : shown;
};
/**
* Reads a bundle produced by `serializeSessionExport`.
*
* Every version mismatch is a hard reject; the major/minor compare only picks
* which message to show, and is not a compatibility policy.
*/
function parseSessionExportBundle(input) {
	const envelope = z.object({ schemaVersion: z.string() }).safeParse(input);
	if (!envelope.success) return err(/* @__PURE__ */ new Error("Not a GitLab Duo session export: the document has no \"schemaVersion\" field."));
	const { schemaVersion } = envelope.data;
	if (!VERSION_PATTERN.test(schemaVersion)) return err(/* @__PURE__ */ new Error(`This session export declares schema version "${schemaVersion}", which is not a recognisable version. This build reads version 1.0.`));
	if (schemaVersion !== "1.0") return err(/* @__PURE__ */ new Error(isNewerThanSupported(schemaVersion) ? `This session export uses schema version ${schemaVersion}, which is newer than the version this build supports (1.0). Upgrade GitLab Duo to read it.` : `This session export uses schema version ${schemaVersion}, which is no longer supported (this build reads version 1.0). Re-export the session.`));
	const parsed = sessionExportBundleSchema.safeParse(input);
	if (!parsed.success) return err(/* @__PURE__ */ new Error(`Malformed session export bundle — ${describeIssues(parsed.error)}`));
	return ok(parsed.data);
}
//#endregion
//#region src/session_export/session_export_filter.ts
/** The tool the backend emits when it compacts the context window. */
const COMPACTION_TOOL_NAME = "compaction";
const isCompactionEntry = (entry) => entry.message_type === "tool" && entry.tool_info?.name === COMPACTION_TOOL_NAME;
/**
* Rejects noise rather than conversation: compaction cards, the agent message
* paired with one, and entries with neither content nor a tool call. Retry
* banners and `InfoMessage` never reach a checkpoint, so nothing filters them.
*/
const isRetained = (entry, index, chatLog) => {
	if (isCompactionEntry(entry)) return false;
	const previous = index > 0 ? chatLog[index - 1] : void 0;
	if (entry.message_type === "agent" && previous && isCompactionEntry(previous)) return false;
	return entry.content.trim().length > 0 || entry.tool_info !== null;
};
/**
* Positions in the *original* log: an id synthesized from the filtered position
* would shift with whatever else was dropped.
*/
function retainedChatLogIndices(chatLog) {
	return chatLog.reduce((retained, entry, index) => {
		if (isRetained(entry, index, chatLog)) retained.push(index);
		return retained;
	}, []);
}
/**
* `slice` counts UTF-16 code units, so cutting mid-pair leaves a lone surrogate.
* Grapheme clusters (what a user would call one character) are left alone: splitting
* one is cosmetic, where splitting a codepoint produces invalid text.
*/
const snapToCodepointBoundary = (text, cut) => {
	const last = text.charCodeAt(cut - 1);
	return last >= 55296 && last <= 56319 ? cut - 1 : cut;
};
/**
* A cut inside {@link ROOT_PLACEHOLDER} leaves a fragment `resolveExportedPath`
* can never re-anchor. Exact rather than a suffix guess, so text that
* legitimately ends in `<ro` is left alone.
*/
const snapBeforePartialPlaceholder = (text, cut) => {
	const earliest = Math.max(0, cut - 13 + 1);
	for (let start = earliest; start < cut; start += 1) if (text.startsWith("<gl-duo-root>", start)) return start;
	return cut;
};
/**
* Cuts `text` to at most `maxLength`, leaving a verbatim prefix with no marker
* appended, which would push the payload past the cap it enforces. The cut only
* ever moves earlier, so the result stays within the cap and stays a prefix.
*/
function truncateText(text, maxLength) {
	if (!Number.isInteger(maxLength) || maxLength < 0) throw new Error(`Session export: cap must be an integer >= 0, got ${maxLength}.`);
	const originalLength = text.length;
	if (originalLength <= maxLength) return {
		text,
		truncated: false,
		originalLength
	};
	const cut = snapToCodepointBoundary(text, snapBeforePartialPlaceholder(text, maxLength));
	return {
		text: text.slice(0, cut),
		truncated: true,
		originalLength
	};
}
//#endregion
//#region src/session_export/session_export_summary.ts
const isToolEntry = (entry) => entry.type === "tool";
const isRequestEntry = (entry) => entry.type === "request";
/**
* The only reliable failure signal: a failed tool's entry still carries
* `status: 'success'`. Survives every cap, all of which cut a suffix.
*/
const ACTION_ERROR_PREFIX = "Action error:";
/** The mapper's name for a tool entry the backend sent without a tool call. */
const UNKNOWN_TOOL = "unknown tool";
/** Same fallback order as `WorkflowEventMapperService#extractToolOutput`. */
const outputOf = (entry) => entry.tool?.response?.content ?? entry.content;
const isFailure = (entry) => {
	const { status } = entry.tool?.response ?? {};
	if (status !== void 0 && status !== "success") return true;
	return outputOf(entry).startsWith(ACTION_ERROR_PREFIX);
};
/**
* Raw checkpoint arg keys, not the formatted `ToolInputDisplay` ones:
* `read_file` carries `file_path` on the wire and `filepath` after formatting.
*
* Copied from the formatters in `lib_workflow_executor`, which stay the source
* of truth. Importing them would be a cycle, and they are async anyway.
*/
const READ_PATH_ARGS = {
	read_file: "file_path",
	read_files: "file_paths"
};
const CHANGED_PATH_ARGS = {
	edit_file: "file_path",
	create_file_with_contents: "file_path"
};
const argKeyFor = (table, toolName) => table[toolName];
/** Arg keys `commandOf` reads, per tool. Kept beside it, so the two cannot drift. */
const COMMAND_ARGS = {
	run_command: [
		"command",
		"program",
		"args"
	],
	shell_command: [
		"command",
		"program",
		"args"
	],
	run_git_command: ["command", "args"]
};
/** Keys the budget caps loosely rather than at the payload cap, since `commandsRun` quotes them. */
function summaryArgKeysFor(toolName) {
	return [
		argKeyFor(READ_PATH_ARGS, toolName),
		argKeyFor(CHANGED_PATH_ARGS, toolName),
		...COMMAND_ARGS[toolName] ?? []
	].filter((key) => key !== void 0);
}
const commandOf = (tool) => {
	if (!tool) return [];
	const { args } = tool;
	switch (tool.name) {
		case "run_command":
		case "shell_command":
			if (typeof args.command === "string") return [args.command];
			if (typeof args.program !== "string") return [];
			return [typeof args.args === "string" ? `${args.program} ${args.args}` : args.program];
		case "run_git_command": {
			if (typeof args.command !== "string") return [];
			const git = `git ${args.command}`;
			return [typeof args.args === "string" ? `${git} ${args.args}` : git];
		}
		default: return [];
	}
};
const unique = (values) => [...new Set(values)];
const collectPaths = (executions, argsByTool) => unique(executions.flatMap((entry) => {
	if (isFailure(entry)) return [];
	const key = entry.tool && argKeyFor(argsByTool, entry.tool.name);
	const value = key === void 0 ? void 0 : entry.tool?.args[key];
	if (typeof value === "string") return [value];
	if (Array.isArray(value)) return value.filter((item) => typeof item === "string");
	return [];
}));
/**
* Not the entry id: the two flows relate request and execution ids differently,
* so an id match silently finds nothing on one of them. `JSON.stringify` is
* key-order sensitive, but a mismatch over-reports rather than drops. Oldest-first
* reclamation can trim one of a pair further than the other, landing in that case.
*/
const identityOf = (tool) => tool && `${tool.name} ${JSON.stringify(tool.args)}`;
/** Right-to-left so `recovered` already holds every success after the failure. */
const unresolvedErrors = (executions) => {
	const recovered = /* @__PURE__ */ new Set();
	const unresolved = [];
	for (let index = executions.length - 1; index >= 0; index -= 1) {
		const entry = executions[index];
		const identity = identityOf(entry.tool);
		if (!isFailure(entry)) {
			if (identity) recovered.add(identity);
		} else if (!identity || !recovered.has(identity)) unresolved.push({
			tool: entry.tool?.name ?? UNKNOWN_TOOL,
			entryId: entry.id,
			message: outputOf(entry)
		});
	}
	return unresolved.reverse();
};
const pendingToolCalls = (entries) => {
	const last = entries.at(-1);
	if (!last || !isKnownEntry(last) || !isRequestEntry(last) || !last.tool) return [];
	return [{
		tool: last.tool.name,
		entryId: last.id
	}];
};
/**
* Derives the bundle's `summary` block from its transcript. Pure and deterministic:
* no clock, no sorting, first-seen order throughout.
*
* Takes the *serialized* transcript, not the `ChatLog` behind it, so the summary
* inherits the redaction and anchoring already applied.
*/
function deriveSessionSummary(entries) {
	const known = entries.filter(isKnownEntry);
	const executions = known.filter(isToolEntry);
	const firstEntryAt = entries[0]?.timestamp;
	const lastEntryAt = entries.at(-1)?.timestamp;
	const finalAgentMessage = known.filter((entry) => entry.type === "agent").at(-1)?.content;
	return {
		entryCount: entries.length,
		toolExecutionCount: executions.length,
		...firstEntryAt && { firstEntryAt },
		...lastEntryAt && { lastEntryAt },
		userGoals: known.filter((entry) => entry.type === "user").map((entry) => entry.content),
		filesRead: collectPaths(executions, READ_PATH_ARGS),
		filesChanged: collectPaths(executions, CHANGED_PATH_ARGS),
		commandsRun: unique(executions.flatMap((entry) => commandOf(entry.tool))),
		unresolvedErrors: unresolvedErrors(executions),
		pendingToolCalls: pendingToolCalls(entries),
		...finalAgentMessage !== void 0 && { finalAgentMessage }
	};
}
//#endregion
//#region src/session_export/session_export_budget.ts
/** Of the emitted artifact, indentation and trailing newline included. */
const DEFAULT_MAX_BUNDLE_BYTES = 1048576;
/** Covers `tool.response.content` and `tool.args` strings alike, so neither is the cheap route. */
const DEFAULT_MAX_PAYLOAD_LENGTH = 4e3;
const DEFAULT_MAX_ENTRY_CONTENT_LENGTH = 16e3;
/** Envelope metadata is labels, not content, so it is capped far shorter than a transcript field. */
const DEFAULT_MAX_METADATA_LENGTH = 1024;
/** Below about a kilobyte the recorded-cut metadata costs more than the cut saves. */
const TRIM_FLOOR_LENGTH = 1024;
const DEFAULT_BUNDLE_BUDGET = {
	maxBundleBytes: DEFAULT_MAX_BUNDLE_BYTES,
	maxPayloadLength: DEFAULT_MAX_PAYLOAD_LENGTH,
	maxEntryContentLength: DEFAULT_MAX_ENTRY_CONTENT_LENGTH,
	maxMetadataLength: DEFAULT_MAX_METADATA_LENGTH
};
const assertCap = (name, value, minimum) => {
	if (!Number.isInteger(value) || value < minimum) throw new Error(`Session export budget: ${name} must be an integer >= ${minimum}, got ${value}.`);
};
/** Throws rather than clamping: an unchecked cap emits a bundle this package's parser rejects. */
function assertBundleBudgetPolicy(policy) {
	assertCap("maxBundleBytes", policy.maxBundleBytes, 1);
	assertCap("maxPayloadLength", policy.maxPayloadLength, 0);
	assertCap("maxEntryContentLength", policy.maxEntryContentLength, 0);
	assertCap("maxMetadataLength", policy.maxMetadataLength, 0);
}
const resolveBundleBudget = (overrides = {}) => {
	const policy = {
		...DEFAULT_BUNDLE_BUDGET,
		...overrides
	};
	assertBundleBudgetPolicy(policy);
	return policy;
};
/** Node/bun only; the barrel reaches the webview bundle but nothing there measures. */
const utf8ByteLength = (text) => Buffer.byteLength(text, "utf8");
const sum = (values) => values.reduce((total, value) => total + value, 0);
/** A `transcript` element sits two levels in: the root object, then the array. */
const TRANSCRIPT_DEPTH = 2;
const newlineCount = (text) => {
	let count = 0;
	for (let index = 0; index < text.length; index += 1) if (text.charCodeAt(index) === 10) count += 1;
	return count;
};
const documentBytes = (value) => utf8ByteLength(`${JSON.stringify(value, null, 2)}\n`);
/** Every writer must use this: the budget is measured on exactly these bytes. */
const formatSessionExport = (bundle) => `${JSON.stringify(bundle, null, 2)}\n`;
/** Adds the indentation a nested element carries but the same value stringified alone does not. */
const entryBytes = (value) => {
	const json = JSON.stringify(value, null, 2);
	return utf8ByteLength(json) + 2 * TRANSCRIPT_DEPTH * newlineCount(json);
};
/** Separators plus the newlines an indented array puts around its elements. */
const transcriptFraming = (count) => {
	if (count === 0) return 0;
	const nested = 2 * TRANSCRIPT_DEPTH;
	return (count - 1) * (2 + nested) + 2 + nested + 2 * (TRANSCRIPT_DEPTH - 1);
};
const totalBytes = (sizes) => sum(sizes) + transcriptFraming(sizes.length);
/** RFC 6901, so an unvalidated backend key containing `/` or `~` stays unambiguous. */
const escapePointerSegment = (key) => key.replaceAll("~", "~0").replaceAll("/", "~1");
const capArgValue = (value, maxLength, pointer) => {
	if (typeof value === "string") {
		const { text, truncated, originalLength } = truncateText(value, maxLength);
		return truncated ? {
			value: text,
			cuts: { [pointer]: originalLength }
		} : {
			value,
			cuts: {}
		};
	}
	if (Array.isArray(value)) {
		const cuts = {};
		let changed = false;
		const capped = value.map((item, index) => {
			const next = capArgValue(item, maxLength, `${pointer}/${index}`);
			Object.assign(cuts, next.cuts);
			changed = changed || next.value !== item;
			return next.value;
		});
		return {
			value: changed ? capped : value,
			cuts
		};
	}
	if (value !== null && typeof value === "object") {
		const cuts = {};
		let changed = false;
		const capped = Object.fromEntries(Object.entries(value).map(([key, nested]) => {
			const next = capArgValue(nested, maxLength, `${pointer}/${escapePointerSegment(key)}`);
			Object.assign(cuts, next.cuts);
			changed = changed || next.value !== nested;
			return [key, next.value];
		}));
		return {
			value: changed ? capped : value,
			cuts
		};
	}
	return {
		value,
		cuts: {}
	};
};
const capArgs = (args, caps, summaryKeys) => {
	const cuts = {};
	let changed = false;
	const capped = {};
	Object.entries(args).forEach(([key, value]) => {
		const next = capArgValue(value, summaryKeys.has(key) ? caps.entryContent : caps.payload, `/${escapePointerSegment(key)}`);
		Object.assign(cuts, next.cuts);
		changed = changed || next.value !== value;
		capped[key] = next.value;
	});
	return {
		args: changed ? capped : args,
		cuts
	};
};
const capResponse = (response, maxLength) => {
	const { text, truncated, originalLength } = truncateText(response.content, maxLength);
	if (!truncated) return response;
	return {
		...response,
		content: text,
		truncated: true,
		originalLength: response.originalLength ?? originalLength
	};
};
const capToolCall = (tool, caps) => {
	const { args, cuts } = capArgs(tool.args, caps, new Set(summaryArgKeysFor(tool.name)));
	const response = tool.response && capResponse(tool.response, caps.payload);
	if (args === tool.args && response === tool.response) return tool;
	const argsTruncated = {
		...cuts,
		...tool.argsTruncated
	};
	return {
		...tool,
		args,
		...Object.keys(argsTruncated).length > 0 && { argsTruncated },
		...response && { response }
	};
};
const capContent = (entry, caps) => {
	if (caps.protectUserContent && entry.type === "user") return void 0;
	const { text, truncated, originalLength } = truncateText(entry.content, caps.entryContent);
	if (!truncated) return void 0;
	return {
		content: text,
		contentTruncated: true,
		contentOriginalLength: entry.contentOriginalLength ?? originalLength
	};
};
/** Unknown entry types pass through: this version cannot tell which field is content. */
const capEntry = (entry, caps) => {
	if (!isKnownEntry(entry)) return entry;
	const content = capContent(entry, caps);
	if (entry.type === "user" || entry.type === "agent") return content ? {
		...entry,
		...content
	} : entry;
	const tool = entry.tool && capToolCall(entry.tool, caps);
	if (!content && tool === entry.tool) return entry;
	return {
		...entry,
		...content,
		...tool && { tool }
	};
};
/** Bounds the envelope, which the transcript passes never reach. Per-field reasons on the schema. */
function capSessionEnvelope(envelope, policy) {
	assertBundleBudgetPolicy(policy);
	const { maxMetadataLength } = policy;
	const title = envelope.title === void 0 ? void 0 : truncateText(envelope.title, maxMetadataLength);
	const rootName = truncateText(envelope.rootName, maxMetadataLength);
	return {
		session: {
			id: envelope.id,
			...title && {
				title: title.text,
				...title.truncated && {
					titleTruncated: true,
					titleOriginalLength: title.originalLength
				}
			},
			...envelope.flow !== void 0 && (envelope.flow.length > maxMetadataLength ? { flowOmitted: true } : { flow: envelope.flow })
		},
		workspace: {
			rootName: rootName.text,
			...rootName.truncated && { rootNameTruncated: true }
		}
	};
}
/** Pass 1. Run before `deriveSessionSummary`, so the summary describes what ships. */
function capTranscriptFields(entries, policy) {
	assertBundleBudgetPolicy(policy);
	const caps = {
		payload: policy.maxPayloadLength,
		entryContent: policy.maxEntryContentLength,
		protectUserContent: false
	};
	return entries.map((entry) => capEntry(entry, caps));
}
/** Machine-written bytes first, then user prose: eliding an entry costs more than shortening one. */
const RECLAIM_TIERS = [{
	payload: TRIM_FLOOR_LENGTH,
	entryContent: TRIM_FLOOR_LENGTH,
	protectUserContent: true
}, {
	payload: TRIM_FLOOR_LENGTH,
	entryContent: TRIM_FLOOR_LENGTH,
	protectUserContent: false
}];
/** To the floor, not the exact deficit, so every cut value sits at a cap or the floor. */
const reclaimTier = (current, target, caps) => {
	const entries = [...current.entries];
	const sizes = [...current.sizes];
	let { total } = current;
	for (let index = 0; index < entries.length && total > target; index += 1) {
		const trimmed = capEntry(entries[index], caps);
		const size = trimmed === entries[index] ? sizes[index] : entryBytes(trimmed);
		if (size < sizes[index]) {
			total -= sizes[index] - size;
			entries[index] = trimmed;
			sizes[index] = size;
		}
	}
	return {
		entries,
		sizes,
		total
	};
};
/** Pass 2. A target of zero floors everything, which is what pass 3 wants. */
const reclaimOldestFirst = (entries, sizes, target) => RECLAIM_TIERS.reduce((current, caps) => current.total <= target ? current : reclaimTier(current, target, caps), {
	entries,
	sizes,
	total: totalBytes(sizes)
});
const SUMMARY_OVERHEAD_PER_ENTRY = 128;
/** Doubled because every variable summary field copies transcript bytes; hence only a floor. */
const projectedBytes = (bytes, count) => 2 * bytes + transcriptFraming(count) + SUMMARY_OVERHEAD_PER_ENTRY * count;
/** The goal entry states what the session was for, so it survives a suffix it precedes. */
const suffixWithGoal = (entries, count, goalIndex) => {
	const start = entries.length - count;
	const suffix = entries.slice(start);
	return goalIndex >= 0 && goalIndex < start ? [entries[goalIndex], ...suffix] : suffix;
};
const provableSuffix = (entries, sizes, allowance, goalIndex) => {
	let bytes = goalIndex >= 0 ? sizes[goalIndex] : 0;
	let count = goalIndex >= 0 ? 1 : 0;
	let length = 0;
	for (let index = entries.length - 1; index > goalIndex; index -= 1) {
		if (projectedBytes(bytes + sizes[index], count + 1) > allowance) break;
		bytes += sizes[index];
		count += 1;
		length = entries.length - index;
	}
	return length;
};
/** Pass 3. Adding an older entry only adds bytes, so the fit predicate is monotone and searchable. */
const elideOldestFirst = (entries, sizes, allowance, fits) => {
	const firstUser = entries.findIndex((entry) => isKnownEntry(entry) && entry.type === "user");
	const goalIndex = firstUser >= 0 && projectedBytes(sizes[firstUser], 1) <= allowance ? firstUser : -1;
	let low = provableSuffix(entries, sizes, allowance, goalIndex);
	let high = entries.length;
	while (low < high) {
		const mid = Math.ceil((low + high) / 2);
		if (fits(suffixWithGoal(entries, mid, goalIndex))) low = mid;
		else high = mid - 1;
	}
	const kept = suffixWithGoal(entries, low, goalIndex);
	return {
		entries: kept,
		omitted: entries.length - kept.length
	};
};
const MAX_FIT_ATTEMPTS = 2;
/** Passes 2 and 3. Guarantees bytes <= `maxBundleBytes`, or `budget.exceeded` saying why not. */
function fitBundleToBudget(bundle, policy) {
	assertBundleBudgetPolicy(policy);
	const { maxBundleBytes } = policy;
	const entries = bundle.transcript;
	if (documentBytes(bundle) <= maxBundleBytes) return bundle;
	const sizes = entries.map(entryBytes);
	const widestReport = {
		maxBytes: maxBundleBytes,
		omittedEntryCount: entries.length
	};
	const assemble = (kept, omittedEntryCount) => ({
		...bundle,
		budget: {
			maxBytes: maxBundleBytes,
			omittedEntryCount
		},
		summary: deriveSessionSummary(kept),
		transcript: kept
	});
	const envelopeBytes = (candidate) => documentBytes({
		...candidate,
		budget: widestReport,
		transcript: []
	});
	const skeletonBytes = documentBytes({
		...bundle,
		budget: widestReport,
		summary: deriveSessionSummary([]),
		transcript: []
	});
	if (skeletonBytes > maxBundleBytes) return {
		...assemble(reclaimOldestFirst(entries, sizes, 0).entries, 0),
		budget: {
			maxBytes: maxBundleBytes,
			omittedEntryCount: 0,
			exceeded: true
		}
	};
	let target = maxBundleBytes - envelopeBytes(bundle);
	for (let attempt = 0; attempt < MAX_FIT_ATTEMPTS; attempt += 1) {
		const candidate = assemble(reclaimOldestFirst(entries, sizes, target).entries, 0);
		if (documentBytes(candidate) <= maxBundleBytes) return candidate;
		const tighter = maxBundleBytes - envelopeBytes(candidate);
		if (tighter >= target) break;
		target = tighter;
	}
	const floored = reclaimOldestFirst(entries, sizes, 0);
	const elided = elideOldestFirst(floored.entries, floored.sizes, maxBundleBytes - skeletonBytes, (kept) => documentBytes(assemble(kept, entries.length - kept.length)) <= maxBundleBytes);
	const candidate = assemble(elided.entries, elided.omitted);
	if (documentBytes(candidate) <= maxBundleBytes) return candidate;
	return assemble([], entries.length);
}
//#endregion
//#region src/session_export/session_export_serializer.ts
/**
* A synthetic name, because gitleaks' allowlist skips 25 path patterns before
* any rule runs and a real filename could switch redaction off entirely. The
* cost is the five path-scoped rules, which never fire here.
*/
const REDACTION_SOURCE_LOCATION = "session-export";
const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const stripTrailingSeparators = (path) => path.replace(/[\\/]+$/, "");
const basename = (path) => path.split(/[\\/]/).filter(Boolean).pop() ?? path;
/**
* Anchors absolute paths under `root` to {@link ROOT_PLACEHOLDER}. Both
* boundaries are load-bearing:
*
* - Right, or a sibling is mangled: `/home/user/proj2/x` against root
*   `/home/user/proj` would become `<gl-duo-root>2/x`.
* - Left, or a path whose suffix equals the root is mangled:
*   `/mnt/backup/home/user/proj/f` would become `/mnt/backup<gl-duo-root>/f`.
*
* The left guard tests for a filename character, not a separator, since in
* prose a path is usually preceded by a space or quote. Ambiguous delimiters
* like `(` stay unguarded: guessing wrong on one leaks a whole path, where
* guessing wrong on a name only mangles it.
*/
const buildRootPattern = (root) => new RegExp(`(?<![\\p{L}\\p{N}._~+@-])${escapeRegExp(root)}(?=[\\\\/]|$)`, "gmu");
/** @param rootPattern compiled once per export, since this runs for every string. */
const relativizeToRoot = (text, root, rootPattern) => {
	if (!root || !text.includes(root)) return text;
	return text.replace(rootPattern, ROOT_PLACEHOLDER);
};
/**
* Inverts the anchoring `serializeSessionExport` applies, which is what makes an
* exported path resolvable rather than decorative.
*/
function resolveExportedPath(text, workspaceRoot) {
	return text.replaceAll(ROOT_PLACEHOLDER, stripTrailingSeparators(workspaceRoot));
}
const scrubValue = (value, scrub) => {
	if (typeof value === "string") return scrub(value);
	if (Array.isArray(value)) return value.map((item) => scrubValue(item, scrub));
	if (value !== null && typeof value === "object") return Object.fromEntries(Object.entries(value).map(([key, nested]) => [key, scrubValue(nested, scrub)]));
	return value;
};
const toToolResponse = (raw, scrub) => {
	const isString = typeof raw === "string";
	return {
		content: scrub(isString ? raw : raw.content),
		...isString ? {} : { status: raw.status }
	};
};
const toToolCall = (toolInfo, scrub) => ({
	name: toolInfo.name,
	args: scrubValue(toolInfo.args, scrub),
	...toolInfo.suggested_patterns && { suggestedPatterns: toolInfo.suggested_patterns.map(scrub) },
	...toolInfo.tool_response !== void 0 && { response: toToolResponse(toolInfo.tool_response, scrub) }
});
const toEntry = (entry, index, scrub) => {
	const base = {
		id: entry.message_id ?? `${entry.message_type}-${index}`,
		timestamp: entry.timestamp,
		content: scrub(entry.content),
		...entry.message_sub_type && { subType: entry.message_sub_type },
		...entry.component_name && { agent: scrub(entry.component_name) },
		...entry.subsession_id && { subsessionId: entry.subsession_id }
	};
	if (entry.message_type === "user" || entry.message_type === "agent") return {
		...base,
		type: entry.message_type
	};
	return {
		...base,
		type: entry.message_type,
		...entry.tool_info && { tool: toToolCall(entry.tool_info, scrub) }
	};
};
/**
* Turns a session's `ChatLog` into a portable, redacted export bundle.
*
* Sourced from `ChatLog` rather than the CLI's `SessionEvent`, which would
* couple every future consumer, the IDE included, to the CLI's event model.
*
* Order is load-bearing: cap, derive, then fit, which re-derives if it trims further.
*/
function serializeSessionExport({ sessionId, chatLog, workspaceRoot, redactor, title, flow, exportedAt = /* @__PURE__ */ new Date(), budget }) {
	const root = stripTrailingSeparators(workspaceRoot);
	const rootPattern = buildRootPattern(root);
	const scrub = (text) => redactor.redactSecrets(relativizeToRoot(text, root, rootPattern), REDACTION_SOURCE_LOCATION);
	const policy = resolveBundleBudget(budget);
	const transcript = capTranscriptFields(retainedChatLogIndices(chatLog).map((index) => toEntry(chatLog[index], index, scrub)), policy);
	return fitBundleToBudget({
		schemaVersion: "1.0",
		exportedAt: exportedAt.toISOString(),
		...capSessionEnvelope({
			id: sessionId,
			...title !== void 0 && { title: scrub(title) },
			...flow !== void 0 && { flow },
			rootName: basename(root)
		}, policy),
		summary: deriveSessionSummary(transcript),
		transcript
	}, policy);
}
//#endregion
//#region src/flow_config.ts
function tryParseFlowConfig(flowConfig) {
	if (!flowConfig) return;
	return yaml.load(flowConfig, { schema: yaml.JSON_SCHEMA });
}
//#endregion
//#region src/index.ts
const WorkflowRunner = createInterfaceId("WorkflowRunner");
//#endregion
export { AGENT_PRIVILEGES, AI_CATALOG_VERSION_ID_FRAGMENT, ApprovalSource, BUILD_PRE_APPROVED_AGENT_PRIVILEGES, ChatLogSchema, DEFAULT_MAX_BUNDLE_BYTES, DELEGATE_TASK_TOOL_NAME, DELEGATION_ERROR_SUB_TYPE, DELEGATION_RETURNS_SUB_TYPE, DELEGATION_SUB_TYPE, DELETE_DUO_WORKFLOWS_WORKFLOW, DuoWorkflowStatus, DuoWorkflowStatusEvent, FLOW_METADATA_FRAGMENT, GET_AGENT_FLOW_CONFIG, GET_CONFIGURED_AGENTS, GET_CONFIGURED_AGENTS_18_4_1_AND_EARLIER, GET_CONFIGURED_AGENTS_18_4_2_AND_LATER, GET_LATEST_CHECKPOINT_QUERY, GET_PROJECT_DATA, GET_USER_WORKFLOWS, GET_WORKFLOW_DEFINITION_QUERY, GET_WORKFLOW_ENABLEMENT_CHECKS_QUERY, GET_WORKFLOW_EVENTS_QUERY, GET_WORKFLOW_FLOW_IDENTITY_QUERY, HEADLESS_CLI_PRE_APPROVED_AGENT_PRIVILEGES, MessageType, PLAN_AGENT_PRIVILEGES, ROOT_PLACEHOLDER, SESSION_EXPORT_SCHEMA_VERSION, ToolApprovalType, ToolInfoSchema, ToolResponseSchema, UsageQuotaService, WorkflowDefinition, WorkflowEvent, WorkflowExecutorError, WorkflowFilter, WorkflowMessageSchema, WorkflowPlanApprovalRequestSchema, WorkflowRequestSchema, WorkflowRunner, WorkflowStatusCode, WorkflowStatusCodeMessages, WorkflowSuccessCode, WorkflowToolSchema, createDefaultWorkflowEvent, defaultAgentPrivileges, defaultFlowConfigSchema, deriveSessionSummary, extractUiChatLog, formatSessionExport, generateErrorMessageFromStatusCode, generateGraphqlWorkflowId, getLatestEvent, getStatus, isApprovingPlan, isAtStablePauseBoundary, isAwaitingToolApproval, isAwaitingUserInput, isDuoWorkflowEvent, isInProgressState, isKnownEntry, isNeedsInput, isPlanApprovalRequest, isRunning, isTerminated, isTransientStatusCode, isUserMessage, isWorkflowExecutorErrorEvent, isWorkflowRetryEvent, nodeEventsFrom, parseCheckpoint, parseDelegationMarker, parseLangGraphCheckpoint, parseLatestCheckpointWorkflowData, parseRecordedFlowConfig, parseSessionExportBundle, parseStreamingDelegation, parseWorkflowEventsResponse, resolveExportedPath, resolveWorkflowDefinition, retainedChatLogIndices, serializeSessionExport, sessionExportBundleSchema, summaryArgKeysFor, truncateText, tryParseFlowConfig };

//# sourceMappingURL=index.mjs.map